import { ChatBotUIDateSlot } from "@jugl-web/domain-resources/chat-bot/types";
import { PlainButton } from "@jugl-web/ui-components";
import { cx } from "@jugl-web/utils";
import { useDatePicker } from "@rehookify/datepicker";
import endOfDay from "date-fns/endOfDay";
import startOfDay from "date-fns/startOfDay";
import { FC, useMemo } from "react";
import { ReactComponent as ArrowLeftIcon } from "./assets/arrow-left.svg";
import { ReactComponent as ArrowRightIcon } from "./assets/arrow-right.svg";

interface DaySlotPickerProps {
  slots: ChatBotUIDateSlot[];
  onSelect: (day: Date) => void;
}

export const DaySlotPicker: FC<DaySlotPickerProps> = ({ slots, onSelect }) => {
  const availableDayTimestamps = useMemo<Set<number>>(() => {
    const set = new Set<number>();

    slots.forEach((slot) => {
      set.add(startOfDay(slot.startTime).getTime());
    });

    return set;
  }, [slots]);

  const minDate = useMemo<Date | undefined>(() => {
    if (availableDayTimestamps.size === 0) {
      return undefined;
    }

    const earliest = Math.min(...Array.from(availableDayTimestamps));

    return new Date(earliest);
  }, [availableDayTimestamps]);

  const maxDate = useMemo<Date | undefined>(() => {
    if (availableDayTimestamps.size === 0) {
      return undefined;
    }

    const latest = Math.max(...Array.from(availableDayTimestamps));

    return endOfDay(new Date(latest));
  }, [availableDayTimestamps]);

  const {
    data: { calendars, weekDays },
    propGetters: { subtractOffset, addOffset, dayButton },
  } = useDatePicker({
    selectedDates: [],
    onDatesChange: () => {
      // No-op as the component is fully controlled by the `onSelect` callback
    },
    calendar: {
      // Start from Sunday to fit the American calendar layout
      startDay: 0,
    },
    dates: {
      minDate,
      maxDate,
      mode: "single",
    },
    locale: {
      day: "numeric",
      weekday: "short",
      monthName: "long",
      locale: "en",
    },
  });

  const calendar = calendars[0];

  if (!calendar) {
    return null;
  }

  const { month, year, days } = calendar;

  return (
    <div className="flex flex-col">
      {/* Header */}
      <div className="flex w-full items-center justify-between gap-2">
        <PlainButton
          {...subtractOffset({ months: 1 })}
          className={cx(
            "flex h-8 w-8 shrink-0 items-center justify-center rounded-lg",
            "text-grey cursor-pointer transition",
            "hover:bg-grey-100 active:bg-grey-200 disabled:cursor-not-allowed disabled:opacity-30 disabled:hover:bg-transparent"
          )}
        >
          <ArrowLeftIcon />
        </PlainButton>
        <span className="text-dark font-secondary text-[15px] font-medium leading-[20px]">
          {month}, {year}
        </span>
        <PlainButton
          {...addOffset({ months: 1 })}
          className={cx(
            "flex h-8 w-8 shrink-0 items-center justify-center rounded-lg",
            "text-grey cursor-pointer transition",
            "hover:bg-grey-100 active:bg-grey-200 disabled:cursor-not-allowed disabled:opacity-30 disabled:hover:bg-transparent"
          )}
        >
          <ArrowRightIcon />
        </PlainButton>
      </div>

      {/* Weekday headings */}
      <div className="mt-3 grid grid-cols-7">
        {weekDays.map((weekDay) => (
          <div
            key={`${month}-${year}-${weekDay}`}
            className="font-secondary text-grey flex h-8 w-8 items-center justify-center text-xs leading-[140%]"
          >
            {weekDay.slice(0, 2)}
          </div>
        ))}
      </div>

      {/* Days grid */}
      <div className="mt-[5px] grid grid-cols-7 gap-0.5">
        {days.map((dpDay) => {
          const isCurrentMonth = dpDay.inCurrentMonth;

          const isAvailable =
            isCurrentMonth &&
            availableDayTimestamps.has(startOfDay(dpDay.$date).getTime());

          const { onClick, ...buttonProps } = dayButton(dpDay, {
            disabled: !isAvailable,
          });

          return (
            <button
              key={dpDay.$date.toISOString()}
              type="button"
              className={cx(
                "font-secondary flex h-8 w-8 items-center justify-center rounded-lg border-none bg-transparent text-sm leading-[140%] transition",
                isCurrentMonth && isAvailable
                  ? "text-dark hover:bg-grey-100 active:bg-grey-200 cursor-pointer"
                  : "text-dark-300 cursor-not-allowed line-through",
                !isCurrentMonth && "invisible"
              )}
              onClick={(event) => {
                onClick?.(event);

                if (isAvailable) {
                  onSelect(dpDay.$date);
                }
              }}
              {...buttonProps}
            >
              {dpDay.day}
            </button>
          );
        })}
      </div>
    </div>
  );
};
